import { Component, PLATFORM_ID, inject, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LocalService } from '../../core/services/local.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TenantService } from '../../core/services/tenant.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from '../../core/services/config.service';
import { ThemeService } from '../../core/services/theme.service';
import { SpinnerComponent } from '../../shared/components/ui/spinner/spinner.component';
import { FormFieldComponent } from '../../shared/components/form-field/form-field.component';

@Component({
  selector: 'app-maintenance',
  imports: [
    SpinnerComponent,
    FormFieldComponent,
    CommonModule,
    FormsModule,
    TranslatePipe,
  ],
  templateUrl: './maintenance.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MaintenanceComponent {

  private platformId = inject(PLATFORM_ID);

  showFqdnInput = false;
  loading = false;
  subscription = null;
  error = false;

  fqdn = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private localService: LocalService,
    private tenantService: TenantService,
    public themeService: ThemeService,
    private configService: ConfigService,
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.onHostnameLookup();
    this.themeService.initialize();

    if (this.configService.getTenantUuid()) {
      this.navigatePage();
    }
  }

  changeLang(lang: string) {
    this.translateService.use(lang);
    this.localService.saveData('lang', lang);

    Object.defineProperty(this, 'localeId', { value: lang, configurable: true });
  }

  getLang() {
    return this.translateService.currentLang;
  }

  onHostnameLookup() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.loading = true;
    this.error = false;

    this.subscription = this.tenantService.lookup(window.location.origin)
      .subscribe({
        next: (res: any) => {
          if (res.status) {
            this.initTenantData(res.data);
            this.navigatePage();
          }
          this.loading = false;
        },
        error: (err: any) => {
          this.loading = false;
          this.error = true;
        }
      });
  }

  onSearchFqdn() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.loading = true;
    this.error = false;

    this.subscription = this.tenantService.check(this.fqdn)
      .subscribe({
        next: (res: any) => {

          if (res.status && res?.result?.data?.has_product?.recruitment) {
            this.showFqdnInput = false;
            this.initTenantData(res.result.data);
            this.localService.saveData('tenant.fqdn', this.fqdn);
            this.navigatePage();
          } else {
            this.loading = false;
            this.error = true;
          }
        },
        error: (err: any) => {
          this.loading = false;
          this.error = true;
        }
      });
  }

  initTenantData(data: any) {
    this.localService.saveData('tenant.uuid', data.tenant_uuid);
    this.localService.saveData('tenant.name', data.name || 'Sokrates');
    this.localService.saveData('tenant.logo', data.setting?.logo_url || data.settings?.sokrates?.logo_url || 'https://files.sokrates.co.id/sokrates.png');
    this.localService.saveData('tenant.banner', data.setting?.banner_url || data.settings?.sokrates?.banner_url || null);
    this.changeFavicon(data.setting?.favicon_url || data.settings?.sokrates?.favicon_url || 'https://files.sokrates.co.id/sokrates.png');
  }

  navigatePage() {
    const params = this.route.snapshot.queryParams;
    const preferredLang = this.localService.getData('lang') || 'en';
    const redirect = params['redirect'] ? decodeURIComponent(params['redirect']) : `/${ preferredLang }`;
    this.router.navigateByUrl(redirect);
  }

  changeFavicon(url: string) {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = url;
  }
}
